import { useMemo, useState } from "react"
import { CaretDown, CaretRight } from "@phosphor-icons/react"

import { Badge, Button } from "~/components/design-system/components"
import { api, type RouterOutputs } from "~/utils/api"
import { DateFormatType, formatDate } from "~/utils/datetime"
import type { StoreWithStatus } from "../../useAllocationData"
import { mergeChangelogs, variantIdBatches } from "./productSettingsModel"
import { SETTING_FIELD_META, showSettingValue } from "./productSettingFields"
import type { ProductListing } from "./useProductSettings"

const CHANGELOG_LIMIT = 200

const SOURCE_LABEL = { hand: "Hand", excel: "Excel", auto: "Auto" } as const

type ChangelogEntry = RouterOutputs["allocation"]["getProductSettingsChangelog"][number]

// Module-level, so useQueries only recombines when a batch's result changes.
function combineChangelogReads(
  reads: { data?: ChangelogEntry[]; isLoading: boolean; isError: boolean }[],
) {
  const isLoading = reads.some((read) => read.isLoading)
  return {
    isLoading,
    isError: reads.some((read) => read.isError),
    entries: isLoading
      ? []
      : mergeChangelogs(
          reads.map((read) => read.data ?? []),
          CHANGELOG_LIMIT,
        ),
  }
}

/** The listed variants' setting changes, newest first, read in batches of at most 5,000 variants once opened. */
export default function ProductSettingsChangelog({
  variantIds,
  listings,
  stores,
}: {
  variantIds: string[]
  listings: ReadonlyMap<string, ProductListing>
  stores: StoreWithStatus[]
}) {
  const [open, setOpen] = useState(false)
  const batches = useMemo(() => variantIdBatches(variantIds), [variantIds])
  const { entries, isLoading, isError } = api.useQueries(
    (t) =>
      batches.map((ids) =>
        t.allocation.getProductSettingsChangelog(
          { variantIds: ids, limit: CHANGELOG_LIMIT },
          { enabled: open },
        ),
      ),
    { combine: combineChangelogReads },
  )

  const storeName = useMemo(
    () => new Map(stores.map((store) => [store.allocationLocationId, store.storeNumber])),
    [stores],
  )
  const sizeOf = useMemo(
    () =>
      new Map(
        [...listings.values()].flatMap((listing) =>
          listing.skus.map((sku) => [sku.skuId, sku.size] as const),
        ),
      ),
    [listings],
  )

  return (
    <section className="shrink-0 border-t border-base">
      <Button
        size="sm"
        variant="transparent"
        className="label-sm w-full justify-start rounded-none px-3 text-fg-muted uppercase"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? <CaretDown /> : <CaretRight />}
        Changelog
      </Button>
      {open && (
        <div className="flex max-h-56 flex-col gap-1 overflow-auto px-3 pb-3">
          {variantIds.length === 0 && (
            <div className="paragraph-xs text-fg-muted">No variant is listed.</div>
          )}
          {variantIds.length > 0 && isLoading && (
            <div className="paragraph-xs text-fg-muted">Loading changes...</div>
          )}
          {isError && (
            <div className="paragraph-xs text-fg-muted">The changes could not be loaded.</div>
          )}
          {!isLoading && !isError && variantIds.length > 0 && entries.length === 0 && (
            <div className="paragraph-xs text-fg-muted">
              No settings have been saved for the listed variants yet.
            </div>
          )}
          {entries.map((entry, index) => {
            const listing = listings.get(entry.variantId)
            const variant = listing
              ? [listing.productName, listing.variantName].filter(Boolean).join(" · ")
              : entry.variantId
            const where =
              entry.level === "variant"
                ? "All stores"
                : `${sizeOf.get(entry.skuId) ?? entry.skuId} · ${storeName.get(entry.locationId) ?? entry.locationId}`
            return (
              <div
                key={`${entry.at.getTime()}:${entry.variantId}:${where}:${entry.field}:${index}`}
                className="flex items-baseline gap-2.5"
              >
                <Badge size="sm" variant={entry.source === "hand" ? "gray" : "blue"}>
                  {SOURCE_LABEL[entry.source]}
                </Badge>
                <span className="paragraph-xs min-w-0 flex-1 truncate text-fg-subtle">
                  {variant} · {where} · {SETTING_FIELD_META[entry.field].label}
                </span>
                <span className="paragraph-xs tabular-nums text-fg-muted line-through">
                  {showSettingValue(entry.field, entry.from)}
                </span>
                <span className="paragraph-xs text-fg-muted">→</span>
                <span className="paragraph-xs font-semibold tabular-nums">
                  {showSettingValue(entry.field, entry.to)}
                </span>
                <span className="paragraph-xs w-36 shrink-0 truncate text-right text-fg-muted">
                  {entry.updatedByName}
                </span>
                <span className="paragraph-xs w-32 shrink-0 text-right tabular-nums text-fg-muted">
                  {formatDate(entry.at, DateFormatType.WithTime)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
